import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { SavedAccount } from '../model';
import { localStorageKeys } from '../storageUtils';

function readFavorites(): SavedAccount[] {
  const raw = window.localStorage.getItem(localStorageKeys.FAVORITED);
  return raw ? JSON.parse(raw) : [];
}

function writeFavorites(favorites: SavedAccount[]): void {
  window.localStorage.setItem(localStorageKeys.FAVORITED, JSON.stringify(favorites));
}

export function useFavorites() {
  const queryClient = useQueryClient();
  const query = useQuery<SavedAccount[]>({
    queryKey: ['favorites'],
    queryFn: () => readFavorites(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === localStorageKeys.FAVORITED) {
        queryClient.invalidateQueries({ queryKey: ['favorites'] });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [queryClient]);

  return query;
}

export function useFavoriteActions() {
  const queryClient = useQueryClient();

  const addFavorite = useMutation<SavedAccount[], unknown, SavedAccount>({
    mutationFn: async (account: SavedAccount) => {
      const current = readFavorites();
      const exists = current.some((a) => a.id === account.id);
      const next: SavedAccount[] = exists ? current : [account, ...current];
      writeFavorites(next);
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['favorites'], next);
    },
  });

  const removeFavorite = useMutation<SavedAccount[], unknown, SavedAccount>({
    mutationFn: async (account: SavedAccount) => {
      const current = readFavorites();
      const next: SavedAccount[] = current.filter((a) => a.id !== account.id);
      writeFavorites(next);
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['favorites'], next);
    },
  });

  return { addFavorite, removeFavorite } as const;
}
